/**
 * @author      david.loehr@zumper.com (David Loehr)
 * @copyright   Copyright (c) 2023, Zumper
 * @description TourTypeAndTimePicker
 */

import { type ButtonProps, HStack, Image, Spinner, Text, chakra } from '@chakra-ui/react'
import { useMount } from '@zcode-ui/hook'
import { calendarBlue850 } from '@zcode-ui/icons'
import { selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import { TOUR_BOOKING_IMPRESSION } from '@zcode-ui/web-zumper-integrations'
import { CombinationDropdown, type InputWithPopoverProps } from '@zcode-ui/web-zumper-ui'
import type { SerializableDate } from '@zcode/bizos'
import { IntegratedTourType } from '@zcode/enums'
import type { Origin } from '@zcode/nr-utils'
import type { Listable, Property } from '@zcode/state-type'
import { formatInTimeZone } from 'date-fns-tz'
import type { FormikErrors, FormikTouched } from 'formik'
import React, { cloneElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  receiveSelectedDatetime,
  receiveSelectedTourType,
} from '../../../../modules/multifamilyTourTimePicker/actions/index'
import { selectSelectedTourType } from '../../../../modules/multifamilyTourTimePicker/selectors/index'
import { trackDetailEvent } from '../../../../modules/traktor/thunks/trackDetailEvent'
import { useDispatch } from '../../../../modules/useDispatch'
import { DEFAULT_TZ, toDateStringInTz } from '../TourAvailabilityPicker/helpers'
import { useGetAllAvailableTourDates } from '../TourAvailabilityPicker/hooks/useGetAllAvailableTourDates'
import { useTourTimeOptions } from '../TourAvailabilityPicker/hooks/useTourTimeOptions'
import { getTourTypeLabel } from '../TourOptions/helpers/tourTypeLabels'
import { useTourTypeOptions } from '../TourOptions/helpers/useTourTypeOptions'
import { useHasInstantTourBooking } from '../TourOptions/useHasInstantTourBooking'
import type { SelectedDateAndTime } from '../helpers/getValidationSchema'

const TOUR_TYPE_DESCRIPTIONS_DESKTOP = {
  [IntegratedTourType.AGENT_GUIDED]: 'agent on-site',
  [IntegratedTourType.SELF_GUIDED]: 'no agent on-site',
  [IntegratedTourType.LIVE_VIRTUAL]: 'video call with an agent',
} as const
const TOUR_TYPE_DESCRIPTIONS_MOBILE = {
  [IntegratedTourType.AGENT_GUIDED]: 'Tour with an on-site agent',
  [IntegratedTourType.SELF_GUIDED]: 'Tour at your own pace',
  [IntegratedTourType.LIVE_VIRTUAL]: 'Tour via video call with an agent',
} as const

const formatTime = (date: Date | SerializableDate, tz: string) =>
  formatInTimeZone(date.getTime(), tz, 'h:mm a').toLowerCase()

export interface TourTypeAndTimePickerProps {
  entity: Listable | Property
  errors: FormikErrors<{ selectedDateTime: SelectedDateAndTime | null }>
  onTrackConversion: (
    event:
      | React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void
  origin: Origin
  selectedDateTime: SelectedDateAndTime | null
  setFieldValue: (
    name: 'selectedDateTime',
    value: SelectedDateAndTime,
    shouldValidate?: boolean | undefined
  ) => void
  submitButton: React.ReactElement<ButtonProps>
  submitCount: number
  touched: FormikTouched<{
    selectedDateTime: SelectedDateAndTime | null
  }>
}

function assertValidTourType(
  tourType: number
): asserts tourType is (typeof IntegratedTourType)[keyof typeof IntegratedTourType] {
  for (const value of Object.values(IntegratedTourType)) {
    if (typeof value === 'number' && tourType === value) {
      return
    }
  }
  throw new Error(`${tourType} is not a valid IntegratedTourType`)
}

export const TourTypeAndTimePicker = ({
  entity,
  errors,
  onTrackConversion,
  origin,
  selectedDateTime,
  setFieldValue,
  submitButton,
  submitCount,
  touched,
}: TourTypeAndTimePickerProps) => {
  const { hasInstantTourAvailability } = useHasInstantTourBooking(entity)
  const { dates: allAvailableInstantTourDates } = useGetAllAvailableTourDates(entity)
  const dispatch = useDispatch()
  const isMobile = useSelector(selectIsMobile)
  useMount(() => {
    dispatch(
      trackDetailEvent({ additionalProps: { origin }, entity, name: TOUR_BOOKING_IMPRESSION })
    )
  })
  const [tentativeTourType, setTentativeTourType] = useState<IntegratedTourType | undefined>()
  const onTentativeTourTypeChange = useCallback(
    ({ currentTarget: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
      const tourType = Number(value)
      assertValidTourType(tourType)
      setTentativeTourType(tourType)
    },
    []
  )
  const onClearTentativeValue = useCallback(() => {
    setTentativeTourType(undefined)
  }, [])
  const onTourTypeChange = useCallback(
    ({ currentTarget: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
      const tourType = Number(value)
      assertValidTourType(tourType)
      setTentativeTourType(undefined)
      dispatch(receiveSelectedTourType(tourType))
    },
    [dispatch]
  )
  const _tourTypeOptions = useTourTypeOptions(entity)
  const tourTypeOptions = useMemo(
    () =>
      _tourTypeOptions.fetching
        ? []
        : _tourTypeOptions.tourTypeOptions.map((value) => ({
            displayValue: getTourTypeLabel(value, !isMobile),
            label: (
              <>
                {getTourTypeLabel(value)}
                <chakra.span
                  display={{
                    base: 'none',
                    lg: 'inline',
                  }}
                >
                  {' '}
                  ({TOUR_TYPE_DESCRIPTIONS_DESKTOP[value]})
                </chakra.span>
              </>
            ),
            value,
          })),
    [_tourTypeOptions, isMobile]
  )
  const tourTypeDescriptions = useMemo(
    () => tourTypeOptions.map(({ value }) => TOUR_TYPE_DESCRIPTIONS_MOBILE[value]),
    [tourTypeOptions]
  )
  const selectedTourType = useSelector(selectSelectedTourType)
  useEffect(() => {
    if (
      tourTypeOptions.length &&
      !tourTypeOptions.some(({ value }) => value === selectedTourType)
    ) {
      // If the selected tour type isn't a valid choice, pick the first (in-person is better than
      // self-guided is better than virtual) available tour type
      dispatch(receiveSelectedTourType(tourTypeOptions[0].value))
    }
  }, [dispatch, selectedTourType, tourTypeOptions])

  const { fetching: fetchingTimes, tourTimeOptionsByDate } = useTourTimeOptions(
    entity,
    tentativeTourType ?? selectedTourType
  )
  const tz = entity.tz ?? DEFAULT_TZ
  const tourTimeOptions = useMemo(
    () =>
      (selectedDateTime &&
        tourTimeOptionsByDate?.get(selectedDateTime.selectedDate)?.map((value) => {
          const displayValue = formatTime(value, tz)
          return {
            displayValue,
            label: displayValue,
            value: value.getTime(),
          }
        })) ??
      ([
        {
          displayValue: fetchingTimes ? 'Loading availability…' : 'No availability',
          label: 'N/A',
          value: 0,
        },
      ] as const),
    [fetchingTimes, selectedDateTime, tourTimeOptionsByDate]
  )
  const setTourTime = useCallback(
    (date: Date) => {
      dispatch(receiveSelectedDatetime(date))
      setFieldValue('selectedDateTime', {
        selectedDate: toDateStringInTz(date, entity.tz),
        selectedTime: date,
      })
    },
    [dispatch, entity.tz, setFieldValue]
  )
  useEffect(() => {
    const selectedDateString = selectedDateTime?.selectedDate
    if (tourTimeOptionsByDate) {
      const availableDateStrings = Array.from(tourTimeOptionsByDate.keys())
      if (
        availableDateStrings.length &&
        (!selectedDateString ||
          (!availableDateStrings.includes(selectedDateString) &&
            (!allAvailableInstantTourDates.size ||
              !allAvailableInstantTourDates.has(selectedDateString))))
      ) {
        // If no date is selected, or the selected day is unavailable,
        // select the earliest available date & time
        setTourTime(
          Array.from(tourTimeOptionsByDate.values())
            .flat()
            .sort((a, b) => a.getTime() - b.getTime())[0]
        )
      }
    }
  }, [setTourTime, selectedDateTime, tourTimeOptionsByDate])
  useEffect(() => {
    if (
      selectedDateTime &&
      tourTimeOptions[0].label !== 'N/A' &&
      !tourTimeOptions.some(({ value }) => value === selectedDateTime.selectedTime.getTime())
    ) {
      // The selected time isn't available on the selected day
      // Change to the earliest available time on the selected day
      setTourTime(new Date(tourTimeOptions[0].value))
    }
  }, [selectedDateTime, setTourTime, tourTimeOptions])
  const onTourTimeChange = useCallback(
    ({ currentTarget: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
      const milliseconds = Number(value)
      if (milliseconds) {
        setTourTime(new Date(milliseconds))
      }
    },
    [setTourTime]
  )
  const timeError = useMemo(() => {
    const error = errors?.selectedDateTime
    const touch = touched?.selectedDateTime
    return typeof error !== 'string' &&
      // @ts-expect-error touch "is undefined"
      ((typeof touch !== 'boolean' && touch?.selectedTime) || submitCount) &&
      // @ts-expect-error touch "is undefined"
      ((typeof touch !== 'boolean' && touch?.selectedTime) || submitCount) &&
      // @ts-expect-error error "is undefined"
      typeof error?.selectedTime === 'string'
      ? // @ts-expect-error error "is never"
        error.selectedTime
      : undefined
  }, [errors.selectedDateTime, submitCount, touched.selectedDateTime])

  if (!hasInstantTourAvailability) {
    return null
  }

  const timeInputProps: InputWithPopoverProps<number>['inputProps'] = {
    isDisabled: tourTimeOptions[0].value === 0,
    'aria-label': 'tour time',
  }
  if (fetchingTimes) {
    timeInputProps.hideRightElement = false
    timeInputProps.rightElement = <Spinner />
  }

  const noAvailableTimes = `No ${getTourTypeLabel(
    tentativeTourType ?? selectedTourType,
    true
  ).toLowerCase()}s are available on ${
    selectedDateTime
      ? formatInTimeZone(selectedDateTime.selectedTime.getTime(), tz, 'EEE, MMM d')
      : 'this date'
  }. Try a different date or tour type.`

  return (
    <>
      <HStack<'div'> spacing={0}>
        <CombinationDropdown
          inputWithPopoverProps={[
            {
              descriptions: tourTypeDescriptions,
              inputProps: {
                'aria-label': 'tour type',
                isDisabled: tourTypeOptions.length < 2,
                sx: {
                  'input:disabled': {
                    color: 'gray.950',
                    opacity: 1,
                    '-webkit-text-fill-color': 'var(--chakra-colors-gray-950)',
                  },
                },
              },
              isMobile,
              onChange: onTourTypeChange,
              onClearTentativeValue,
              onTentativeChange: onTentativeTourTypeChange,
              popoverHeader: 'TOUR TYPE',
              popoverItems: tourTypeOptions,
              value: getTourTypeLabel(selectedTourType, !isMobile),
            },
            {
              columns: 3,
              contentWhenDisabled:
                tourTimeOptions[0]?.value === 0 ? (
                  fetchingTimes ? (
                    'Loading available times…'
                  ) : (
                    <Text color="red.500">{noAvailableTimes}</Text>
                  )
                ) : undefined,
              error: timeError,
              inputProps: timeInputProps,
              isInvalid: !!timeError,
              isMobile,
              onBlur: onTrackConversion,
              onChange: onTourTimeChange,
              placement: 'bottom-end',
              popoverHeader: 'TOUR TIME',
              popoverItems: tourTimeOptions,

              value:
                // @ts-expect-error errors.selectedDateTime "is a string" (but not really)
                errors.selectedDateTime?.selectedTime &&
                tentativeTourType &&
                tourTimeOptions[0]?.value !== 0
                  ? 'No availability'
                  : selectedDateTime && (tourTimeOptions[0]?.value !== 0 || tentativeTourType)
                    ? formatTime(selectedDateTime.selectedTime, tz)
                    : tourTimeOptions[0].displayValue,
            },
          ]}
          slotFooter={
            <>
              {selectedDateTime &&
              (tourTimeOptions[0]?.value !== 0 || tentativeTourType) &&
              // @ts-expect-error errors.selectedDateTime "is a string" (but not really)
              (!errors.selectedDateTime?.selectedTime || !tentativeTourType) ? (
                <Text
                  alignItems="center"
                  bgColor="neutral.100"
                  color="blue.850"
                  display="flex"
                  p="var(--chakra-space-2) var(--chakra-space-6)"
                >
                  <Image alt="" mr={2} src={calendarBlue850} />
                  {getTourTypeLabel(selectedTourType, true)} at{' '}
                  {formatTime(selectedDateTime.selectedTime, tz)}
                </Text>
              ) : null}
              {cloneElement(submitButton, {
                marginBottom: 4,
                marginTop: 4,
                marginLeft: 6,
                marginRight: 6,
                width: 'auto',
              })}
            </>
          }
          splitChangeEvents={!isMobile}
        />
      </HStack>
      {!fetchingTimes && tourTimeOptions[0]?.label === 'N/A' ? (
        <Text color="red.500" mb={{ base: 6, lg: 4 }} mt={2} variant="label12Reg">
          {noAvailableTimes}
        </Text>
      ) : (
        <Text color="gray.600" mb={{ base: 6, lg: 4 }} mt={2} variant="label12Reg">
          Time is in the property’s timezone.
        </Text>
      )}
    </>
  )
}
